/**
 * @category 0.Pinia Stores
 * @subcategory CONTACT
 * @module ContactStore
 */
import type {
  ICallInformationsGetter,
  IContact
} from '@/types/contact';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

export const useContactStore = defineStore('contact', () => {
  const contactIdState = ref('noContact');
  const pdlButtonLoadingState = ref(false);
  // Stack to manage contact order (LIFO - Last In First Out)
  const contactIdStack = ref<string[]>([]);
  // Snapshot of the last dropped contact, saved before deletion in contactOnDestroy.
  // Needed because onDestroy fires immediately after onEnded and removes the contact
  // from contactMapState before the agent can click the LineDrop button.
  // AWSCONNECT-202: https://storm-it-dig.atlassian.net/browse/AWSCONNECT-202
  const lastDroppedContactState = ref<IContact | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contactMapState: {[key: string]: any} = reactive({
    noContact: {
      contactId: 'noContact',
      Frazionario: '',
      contactPDL: '',
      phoneNumber: '',
      Source: 'ccp-web',
      channel: 'VOICE'
    }
  });

  const currentContactGetter = computed((
  ): IContact => {
    return contactMapState[contactIdState.value];
  });

  const callInformationsGetter = computed((
  ): ICallInformationsGetter[] => {
    const currentContact = currentContactGetter.value;

    const {
      CodUser,
      Nome,
      Cognome,
      Email,
      contactEmail,
      Frazionario,
      Matricola,
      phoneNumber,
      Telefono,
      Ufficio,
      contactPDL,
      currentQueue,
      hasManualSearchData
    } = currentContact;

    const lastUpdatedDate = currentContact.LastUpdateDate;
    const machine = currentContact.MachineName as string;
    const SDP = currentContact.SDPVersion;
    const section = currentContact.Sezione;

    return [
      { title: 'get_data', dataInfo: { Frazionario, contactPDL } },
      {
        title: 'office_info',
        officeInfo: {
          office: hasManualSearchData ? Ufficio : (Ufficio || undefined),
          officePhone: hasManualSearchData ? Telefono as string : (Telefono as string || undefined)
        }
      },
      {
        title: 'caller_info',
        callerInfo: {
          name: hasManualSearchData ? Nome : (Nome || undefined),
          surname: hasManualSearchData ? Cognome : (Cognome || undefined),
          currentQueue: currentQueue,
          serialNumber: hasManualSearchData ? Matricola as string : (Matricola as string || undefined),
          codUser: hasManualSearchData ? CodUser : (CodUser || undefined),
          email: hasManualSearchData ? Email : ((Email || contactEmail) || undefined),
          phone: phoneNumber
        }
      },
      { title: 'pdl_info', pdlInfo: { lastUpdatedDate, machine, SDP, section } }
    ];
  });

  /**
   * @function addCurrentContactToContactMapStateAction
   * @param {IContact} currentContact
   * @returns {{ currentContactState: IContact }}
   * @desc Adds a key for each managed contactId to the *contactMapState* and returns the current one.
   * Pushes the new contactId to the stack to maintain contact order.
   *
   * 'noContact' is the idle placeholder (shown when offline, or via a manual
   * PDLSearch while no real contact is active) and is deliberately never
   * pushed to the stack: it isn't a real contact to "go back to" once one
   * closes, and doing so used to make closing the only active contact bring
   * back whatever stale data a manual search had left on 'noContact' instead
   * of a blank caller section.
   *
   * **Invoked by:**
   * - ##### [setCurrentContact() - utils/contact.ts](module-utils-contact.html#~setCurrentContact)
   */
  const addCurrentContactToContactMapStateAction = (
    currentContact: IContact
  ) => {
    const { contactId } = currentContact;
    contactIdState.value = contactId;

    contactMapState[contactId] = currentContact;

    // Push new contact to stack if not already present
    if (contactId !== 'noContact' && !contactIdStack.value.includes(contactId)) {
      contactIdStack.value.push(contactId);
      console.log('Contact added to stack:', contactId, 'Stack:', contactIdStack.value);
    }

    return { currentContactState: contactMapState[contactId] };
  };

  return {
    contactIdState,
    contactIdStack,
    contactMapState,
    pdlButtonLoadingState,
    lastDroppedContactState,
    currentContactGetter,
    callInformationsGetter,
    addCurrentContactToContactMapStateAction
  };
});
